/**
 * Legacy route. The separate "manual selfie" capture was folded into the
 * liveness screen, so this only forwards.
 *
 * It forwards after a paint, though, and it used to paint `{null}`. Inside a
 * mounted route `null` is not "nothing" — it is a full-screen blank in the
 * window background, white in both themes. That is indistinguishable from the
 * blank-screen failure mode `ConfigErrorScreen` is the safety net for, and it
 * is exactly what a deep link or an old bookmark into this URL lands on while
 * the replace is still in flight.
 *
 * It now paints the destination's own header plus `GateFallback`, the house
 * component for "a redirect is in flight" (components/ui/GateFallback.tsx):
 * themed background, branded spinner, and a `progressbar` role so the
 * transition is announced rather than silent. The hand-off reads as one screen
 * settling instead of two screens flashing.
 */
import { useEffect } from 'react'
import { useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { GateFallback } from '@/components/ui/GateFallback'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { ScreenHeader } from '@/components/ui/ScreenHeader'

export default function SelfieManualRedirect() {
  const router = useRouter()
  const { t } = useTranslation()

  useEffect(() => {
    router.replace('/(tabs)/profile/verification/selfie' as never)
  }, [router])

  // `variant="reading"` matches selfie.tsx's column, so the header does not
  // jump sideways on tablet/desktop when the replace lands. `scroll={false}`
  // because there is nothing to scroll for the frames this route exists.
  return (
    <ScreenContainer variant="reading" scroll={false}>
      {/* Deliberately no `backTestID`: `selfie.back` belongs to the destination
          header, and duplicating it for the handful of frames this route is
          mounted would give the E2E selectors two matches. */}
      <ScreenHeader title={t('verification.selfie_title')} />
      <GateFallback
        accessibilityLabel={t('verification.gate_redirecting')}
        testID="verification.selfie-manual-redirect"
      />
    </ScreenContainer>
  )
}
